import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import {
  signInWithPopup, signInWithCustomToken, signOut, onAuthStateChanged,
} from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import {
  createGameState, dispatch, allocateAction, isBot, startDayAction, doneAction, undoneAction, undoAction, actAction,
  resolveEventAction, continueAction, nightNextAction, roleMayAct, rolesOf, ACTIONS, voteFlipAction, voteAction, renameAction, needAction,
} from '@/engine/game';
import { yearOf } from '@/engine/calendar';
import { makeTutorialGame, clearTutorialTraces } from '@/tutorial/sandbox';
import { PHASES, ROLES } from '@/engine/constants';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, setRoomBots, bindRoom, commitAction, claimRole, noteYear, readBook,
  sweepMyRooms, forgetCampaign, seqOf, watchOpenRooms, friendsGames,
} from '@/store/multiplayer';

/**
 * When a move made on a stale copy of the room may still be played on top of
 * whatever landed first (see playOnline). An ACT, unless its own role has
 * moved since. At dawn, the split's nudges and Start the day: they are
 * deltas and a go, and they used to race each other - "same as yesterday"
 * sends several nudges at once and only the first survived, and Start the
 * day tapped while a nudge was in flight was dropped, so it took two taps
 * (playtest JTk3x).
 */
const rebaseFor = (action, expectedSeq, phase) => {
  if (action.type === ACTIONS.ACT) {
    return (current) => !(current.log || []).some((l) => (l.seq ?? 0) >= expectedSeq && l.role === action.role)
      && seqOf(current) > expectedSeq && current.meta?.phase === phase;
  }
  // A vote is one person's own say: it never depends on anyone else's.
  if ([ACTIONS.VOTE, ACTIONS.VOTE_FLIP, ACTIONS.RENAME, ACTIONS.NEED].includes(action.type)) {
    return (current) => seqOf(current) > expectedSeq && current.meta?.phase === phase;
  }
  if (action.type === ACTIONS.ALLOCATE || action.type === ACTIONS.START_DAY) {
    return (current) => seqOf(current) > expectedSeq && current.meta?.phase === PHASES.DAWN && phase === PHASES.DAWN;
  }
  return null;
};

// The single place engine dispatch() is called from. Components never touch
// engine state directly.

const SAVE_KEY = 'commonwealth:saved-game';
const SETTINGS_KEY = 'commonwealth:settings';
// The room this screen was in, so a refresh (or a phone that fell asleep
// and reloaded) walks straight back into the game instead of the menu.
const ROOM_KEY = 'commonwealth:room';
// ...but only for a sitting. Remembered forever, a bookmark opened next week
// walked straight back into LAST week's room (playtest, 2026-10-15: "that's
// obviously too sticky"). `at` is when this screen last heard from the room;
// past the TTL the memory is dropped and the screen starts at the menu. A
// memory from before `at` existed counts as fresh, so nobody in tonight's
// game is thrown out by this deploy.
const ROOM_TTL = 12 * 60 * 60 * 1000;
const ROOM_TOUCH = 10 * 60 * 1000;
const LOCAL_PLAYER = 'local';

let unbindRoomFn = null;
let unwatchStatusFn = null;

const loadJson = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const touchRoom = (id, mode) => {
  try { window.localStorage.setItem(ROOM_KEY, JSON.stringify({ id, mode, at: Date.now() })); } catch { /* storage off */ }
};

// Called once at load, before anything reads the memory.
const dropStaleRoom = () => {
  try {
    const raw = window.localStorage.getItem(ROOM_KEY);
    const room = raw ? JSON.parse(raw) : null;
    if (!room?.id) return;
    if (!room.at) touchRoom(room.id, room.mode);
    else if (Date.now() - room.at > ROOM_TTL) window.localStorage.removeItem(ROOM_KEY);
  } catch { /* storage off */ }
};
if (typeof window !== 'undefined') dropStaleRoom();

const saveJson = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage off - play on
  }
};

/**
 * May this player send this action? Co-op, so most things are everyone's:
 * the order, an event choice, the continue. A role's TURN is the one thing
 * that belongs to whoever holds the role.
 */
export const mayAct = (game, playerId, action) => {
  if (!game || !playerId) return false;
  if (['ACT', 'DONE', 'UNDONE', 'UNDO', 'NEED'].includes(action.type)) {
    // A bot's role is moved by the screen that drives the bots (the host's).
    if (isBot(game.roles[action.role])) return true;
    return game.roles[action.role] === playerId;
  }
  // Your vote is yours alone.
  if (action.type === 'VOTE' || action.type === 'VOTE_FLIP' || action.type === 'RENAME') return action.by === playerId && game.players.some((p) => p.id === playerId);
  return game.players.some((p) => p.id === playerId);
};

import { botDriver } from '@/bots/driver';

export default createStore({
  plugins: [botDriver],
  state: {
    game: null,
    // 'phone' is a player's own screen; 'table' is the shared big screen.
    mode: 'phone',
    settings: loadJson(SETTINGS_KEY) || { playerName: 'You' },
    hasSavedGame: Boolean(loadJson(SAVE_KEY)),

    firebaseConfigured,
    authReady: !firebaseConfigured,
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,

    roomId: null,
    roomMeta: null,
    campaigns: [],
    // Friends' games on the landing: everybody you have played with, and
    // the open lobbies (multiplayer.js OPEN).
    friends: [],
    openRooms: {},
    presence: {},
    roomError: null,

    // THE TUTORIAL: while it runs, `game` is a practice copy on this phone
    // and the real game waits in `stash` (still kept up to date by the
    // room). Nothing played in practice is ever sent or saved.
    tutorial: null,
  },

  getters: {
    friendsGames: (state) => friendsGames(state.openRooms, state.friends, state.user?.uid),
    inPlay: (state) => Boolean(state.game) && state.game.meta.phase !== PHASES.FINISHED,
    isFinished: (state) => Boolean(state.game) && state.game.meta.phase === PHASES.FINISHED,
    isOnline: (state) => Boolean(state.roomId),
    isTable: (state) => state.mode === 'table',
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),

    // Who am I in the game: online, my uid; locally, the one local player.
    myPlayerId: (state) => (state.roomId ? state.user?.uid || null : LOCAL_PLAYER),
    myRoles: (state, getters) => (state.game && state.mode !== 'table' ? rolesOf(state.game, getters.myPlayerId) : []),
    // The day is simultaneous: "my turn" is any of my roles still at work.
    isMyTurn: (state, getters) => getters.myRoles.some((r) => roleMayAct(state.game, r)),
    roleMayAct: (state) => (role) => roleMayAct(state.game, role),
    playerName: (state) => (id) => state.game?.players.find((p) => p.id === id)?.name || '',
  },

  mutations: {
    setGame(state, game) {
      state.game = game;
      state.hasSavedGame = Boolean(game);
      saveJson(SAVE_KEY, game);
    },
    clearGame(state) {
      state.game = null;
      state.hasSavedGame = false;
      saveJson(SAVE_KEY, null);
    },
    setMode: (state, mode) => { state.mode = mode; },
    setSettings(state, settings) {
      state.settings = { ...state.settings, ...settings };
      saveJson(SETTINGS_KEY, state.settings);
    },
    setAuthReady: (state, v) => { state.authReady = v; },
    setUser: (state, user) => { state.user = user; },
    setSiteUser: (state, su) => { state.siteUser = su; state.siteUserReady = true; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setRoom: (state, { id, meta }) => { state.roomId = id; state.roomMeta = meta ?? null; },
    setRoomMeta: (state, meta) => { state.roomMeta = meta; },
    setCampaigns: (state, list) => { state.campaigns = list; },
    setFriends: (state, list) => { state.friends = list; },
    setOpenRooms: (state, rooms) => { state.openRooms = rooms; },
    setPresence: (state, p) => { state.presence = p || {}; },
    setRoomError: (state, e) => { state.roomError = e; },
    setRemoteGame(state, game) {
      if (state.tutorial) { state.tutorial = { ...state.tutorial, stash: game }; return; }
      state.game = game;
    },
    enterTutorial(state, { role, sandbox }) {
      state.tutorial = { role, step: 0, stash: state.game };
      state.game = sandbox;
    },
    setTutorialGame(state, game) { state.game = game; },
    setTutorialStep(state, step) { if (state.tutorial) state.tutorial = { ...state.tutorial, step }; },
    exitTutorial(state) {
      if (!state.tutorial) return;
      state.game = state.tutorial.stash;
      state.tutorial = null;
    },
  },

  actions: {
    // ------------------------------------------------------------ local
    newLocalGame({ commit, state }, { playerName, seed } = {}) {
      const name = playerName || state.settings.playerName || 'You';
      commit('setSettings', { playerName: name });
      commit('setMode', 'phone');
      const players = [{ id: LOCAL_PLAYER, name }];
      commit('setGame', createGameState({ players, seed: seed ?? Date.now() }));
    },

    /** The book: campaigns this person is in. */
    async loadCampaigns({ state, commit }) {
      try {
        const { campaigns, friends } = await readBook({ user: state.user });
        commit('setCampaigns', campaigns);
        commit('setFriends', friends);
      } catch { commit('setCampaigns', []); }
    },
    /** Friends' games: follow the open lobbies while the landing is up. Returns the stop. */
    watchOpenRooms({ commit }) {
      return watchOpenRooms({ onRooms: (rooms) => commit('setOpenRooms', rooms) });
    },
    /** Out of the book: the host deletes the game, anyone else just drops it from their list. */
    async forgetCampaign({ state, commit }, c) {
      await forgetCampaign({
        user: state.user, gameId: c.gameId, roomCode: c.roomCode, host: c.host,
      });
      commit('setCampaigns', state.campaigns.filter((x) => x.gameId !== c.gameId));
    },
    /** Back into a campaign from the book, as a phone or as the table. */
    resumeCampaign({ commit, dispatch: dispatchAction }, { gameId, asTable = false }) {
      commit('setMode', asTable ? 'table' : 'phone');
      dispatchAction('enterRoom', gameId);
    },

    /** Practice a role on a copy of a fresh game (see src/tutorial). */
    startTutorial({ commit, state, getters }, role) {
      if (state.tutorial || state.mode === 'table') return;
      clearTutorialTraces();
      const id = getters.myPlayerId || 'local';
      commit('enterTutorial', { role, sandbox: makeTutorialGame(role, id, state.settings.playerName || 'You') });
    },
    endTutorial({ commit }) {
      commit('exitTutorial');
      clearTutorialTraces();
    },

    resumeGame({ commit }) {
      const saved = loadJson(SAVE_KEY);
      if (!saved) return;
      commit('setMode', 'phone');
      commit('setGame', saved);
    },

    abandonGame({ commit }) { commit('clearGame'); },

    /** Every move, local or online, funnels through here. */
    play({ commit, dispatch: dispatchAction, state, getters }, action) {
      // Practice moves stay on this phone: never sent, never saved.
      if (state.tutorial) {
        if (mayAct(state.game, getters.myPlayerId, action)) commit('setTutorialGame', dispatch(state.game, action));
        return;
      }
      if (state.roomId) { dispatchAction('playOnline', action); return; }
      if (!mayAct(state.game, getters.myPlayerId, action)) return;
      commit('setGame', dispatch(state.game, action));
    },

    allocate({ dispatch: d }, { role, delta }) { d('play', allocateAction(role, delta)); },
    // Playtest 7: a mistap started a day with nobody handed out and six
    // workers went home. The screen refuses it too; this is the door.
    startDay({ dispatch: d, state, getters }, { force = false } = {}) {
      const g = state.game;
      const given = g ? Object.values(g.allocation || {}).reduce((n, v) => n + (v || 0), 0) : 0;
      if (g && g.civ.workers > 0 && given === 0) return;
      d('play', startDayAction(getters.myPlayerId || null, force));
    },
    done({ dispatch: d }, role) { d('play', doneAction(role)); },
    undone({ dispatch: d }, role) { d('play', undoneAction(role)); },
    undo({ dispatch: d }, role) { d('play', undoAction(role)); },
    act({ dispatch: d }, { role, actionId, params }) { d('play', actAction(role, actionId, params)); },
    resolveEvent({ dispatch: d }, choice) { d('play', resolveEventAction(choice)); },
    continueRound({ dispatch: d }) { d('play', continueAction()); },
    // The night, card by card: anyone seated moves every screen on (playtest 12).
    nightNext({ dispatch: d }, round) { d('play', nightNextAction(round)); },
    setNeed({ dispatch: d }, { role, need }) { d('play', needAction(role, need)); },
    renameVillage({ dispatch: d, getters }, { village, name }) { d('play', renameAction(getters.myPlayerId, village, name)); },
    voteFlip({ dispatch: d, getters }, force = false) { d('play', voteFlipAction(getters.myPlayerId, force)); },
    vote({ dispatch: d, getters }, { choice, force = false }) { d('play', voteAction(getters.myPlayerId, choice ?? null, force)); },

    // ------------------------------------------------------------ auth
    initAuth({ commit, dispatch: dispatchAction }) {
      if (!firebaseConfigured || !auth) {
        commit('setAuthReady', true);
        return;
      }
      // The tester URL from `yarn mint-test-token`; a no-op without
      // ?testToken=. Browser automation can't drive the Google popup.
      const params = new URLSearchParams(window.location.search);
      const testToken = params.get('testToken');
      if (testToken) {
        params.delete('testToken');
        const rest = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
        signInWithCustomToken(auth, testToken).catch((error) => commit('setRoomError', error.message));
      }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? { uid: user.uid, displayName: user.displayName, email: user.email } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
          if (user.displayName) dispatchAction('rememberName', user.displayName);
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    rememberName({ commit, state }, name) {
      if (!name || state.settings.playerName === name) return;
      commit('setSettings', { playerName: name });
    },

    async signIn({ commit }) {
      if (!auth) throw new Error('Sign-in needs Firebase configured.');
      try {
        markInteractiveSignIn();
        await signInWithPopup(auth, googleProvider);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async signOutOfSite({ commit, dispatch: dispatchAction }) {
      await dispatchAction('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ------------------------------------------------------------ rooms
    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    // asTable: the big screen opens the room and hosts it without taking a
    // seat, so everyone else scans in from their phone.
    async hostRoom({ commit, state, dispatch: dispatchAction }, { asTable = false, name = '' } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        commit('setMode', asTable ? 'table' : 'phone');
        const { gameId } = await createRoom({ user: state.user, asTable, name });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setMode', 'phone');
        commit('setRoomError', error.message);
      }
    },

    async joinRoomByCode({ commit, state, dispatch: dispatchAction }, { code, asTable = false }) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        commit('setMode', asTable ? 'table' : 'phone');
        const { gameId } = await joinRoom({ user: state.user, code, asTable });
        await dispatchAction('enterRoom', gameId);
      } catch (error) {
        commit('setMode', 'phone');
        commit('setRoomError', error.message);
      }
    },

    /** Back into the room this screen was in before a reload, if any. */
    rejoinRoom({ commit, state, dispatch: dispatchAction }) {
      const remembered = loadJson(ROOM_KEY);
      if (!remembered?.id || !state.user || state.roomId) return false;
      commit('setMode', remembered.mode === 'table' ? 'table' : 'phone');
      dispatchAction('enterRoom', remembered.id);
      return true;
    },


    enterRoom({ commit, state, dispatch: dispatchAction }, gameId) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setRemoteGame', null);
      touchRoom(gameId, state.mode);
      let touched = Date.now();
      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => {
          commit('setRoomMeta', meta);
          if (meta === null) dispatchAction('leaveRoom');
        },
        onState: (game) => {
          commit('setRemoteGame', game);
          if (Date.now() - touched > ROOM_TOUCH && state.roomId === gameId) {
            touched = Date.now();
            touchRoom(gameId, state.mode);
          }
        },
        onPresence: (presence) => commit('setPresence', presence),
      });
    },

    async leaveRoom({ commit, state }) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user && state.mode !== 'table') {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      commit('setRemoteGame', null);
      commit('setMode', 'phone');
      saveJson(ROOM_KEY, null);
    },

    async toggleRole({ commit, state }, role) {
      try {
        const take = state.roomMeta?.roles?.[role] !== state.user?.uid;
        await claimRole({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, role, take,
        });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async dealRoom({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // Host-only, in the lobby: bots for the roles nobody claims.
    async setRoomBots({ state }, on) {
      if (!state.roomId) return;
      await setRoomBots({ gameId: state.roomId, on });
    },
    async playOnline({ state, commit }, action) {
      const me = state.user?.uid;
      if (!mayAct(state.game, me, action)) return;
      const expectedSeq = seqOf(state.game);
      const yearBefore = yearOf(state.game?.meta?.round || 1);
      try {
        const { state: after } = await commitAction({
          gameId: state.roomId,
          expectedSeq,
          apply: (current) => (mayAct(current, me, action) ? dispatch(current, action) : null),
          // Four people play the day at once, so another role's move often
          // lands between this phone's last sync and the tap - and the tap
          // was silently dropped: the outcome card showed (Firebase applies
          // it locally first), then the server refused it and it vanished
          // (playtest iv_tr, Brian's haggle). A role's own move does not
          // depend on another role's, so replay it on top - unless its OWN
          // role has moved since, which is a double tap or a stale screen.
          rebase: rebaseFor(action, expectedSeq, state.game?.meta?.phase),
        });
        // The year on the room's meta is what the Load screen lists.
        if (after?.meta?.round && yearOf(after.meta.round) !== yearBefore) noteYear({ gameId: state.roomId, year: yearOf(after.meta.round) });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },
  },
});

export { ROLES };
